// Phase 202.c.4 — Centralised error-toast helper.
// Phase 202.c.5 — Added one-click "Report" action that bundles the full
// diagnostic context into the clipboard + a prefilled mailto link so staff
// never have to type anything when reporting an issue to the academy owner.
//
// Before:  toast.error('Failed')                          ← useless
//          toast.error('Failed to delete invoice')        ← marginally better
// After :  apiErrorToast(err, 'Failed to delete invoice') ← shows real cause:
//          "403 · Access denied — this profile lacks the required 'manage_expenses'
//           permission key. Ask your academy owner to grant it from HRMS …"
//          [Report] button → copies {user, status, detail, page_url, timestamp,
//          user_agent} to clipboard + opens prefilled mailto:contact@out-play.in
//
// Behaviour:
//   1. If the error came from axios and has a structured response body, surfaces
//      `HTTP_STATUS · backend.detail` (truncated to 180 chars so toasts stay readable).
//   2. If the error has no response (network failure / CORS / pre-flight reject)
//      shows `Network · <fallback>` so the user knows it never reached the server.
//   3. If neither, falls back to the caller's message.
//   4. Also logs the full error to console for the developer.
//   5. Returns the toast id so callers can dismiss programmatically if they need to.

import { toast } from 'sonner';

const MAX_DETAIL_LEN = 180;
const SUPPORT_EMAIL = 'contact@out-play.in';
// User-identity localStorage keys to probe in order of preference. Different
// app surfaces save the auth payload under slightly different keys; this list
// covers the canonical ones without requiring callers to pass the user in.
const USER_LS_KEYS = ['outplay-user', 'outplay_user', 'user', 'authUser'];

function pickDetail(payload) {
  if (payload == null) return null;
  if (typeof payload === 'string') return payload;
  if (typeof payload !== 'object') return String(payload);
  // FastAPI-style: { detail: "..." } OR { detail: [{ msg, loc }] } for validation errors
  if (typeof payload.detail === 'string') return payload.detail;
  if (Array.isArray(payload.detail) && payload.detail.length > 0) {
    const first = payload.detail[0];
    if (first && typeof first === 'object' && first.msg) {
      const where = Array.isArray(first.loc) ? first.loc.slice(-1).join('.') : '';
      return where ? `${where}: ${first.msg}` : first.msg;
    }
    return JSON.stringify(first).slice(0, MAX_DETAIL_LEN);
  }
  if (typeof payload.message === 'string') return payload.message;
  if (typeof payload.error === 'string') return payload.error;
  return null;
}

// Phase 202.c.5 — collect a structured diagnostic dump from the running
// browser, the failing request, and the LS-cached user. Used by the "Report"
// action so the academy owner gets every field they need with one click.
function buildDiagnostic(err, fallback) {
  let user = null;
  try {
    for (const k of USER_LS_KEYS) {
      const v = localStorage.getItem(k);
      if (v) {
        try { user = JSON.parse(v); break; } catch { /* not JSON, keep probing */ }
      }
    }
  } catch { /* localStorage blocked (private browsing) */ }

  const cfg = err && err.config ? err.config : {};
  return {
    timestamp: new Date().toISOString(),
    page_url: typeof window !== 'undefined' ? window.location.href : '(server)',
    user: user ? {
      id: user.id,
      email: user.email,
      role: user.role,
      designation: user.designation,
      academy_id: user.academy_id,
      permissions: user.permissions,
    } : null,
    request: {
      method: (cfg.method || 'GET').toUpperCase(),
      url: cfg.url || '(unknown)',
      // Don't dump request body — could contain sensitive form data
    },
    response: err && err.response ? {
      status: err.response.status,
      detail: pickDetail(err.response.data) || fallback,
    } : null,
    network_error: err && !err.response ? (err.message || 'no response') : null,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '(server)',
  };
}

function formatDiagnostic(diag) {
  return [
    `Out-Play Error Report`,
    `=====================`,
    `Time      : ${diag.timestamp}`,
    `Page      : ${diag.page_url}`,
    diag.user ? `User      : ${diag.user.email} (${diag.user.role}${diag.user.designation ? ' / ' + diag.user.designation : ''})` : `User      : (not signed in)`,
    diag.user && diag.user.permissions && diag.user.permissions.length > 0 ? `Scopes    : ${diag.user.permissions.join(', ')}` : `Scopes    : (none)`,
    diag.user ? `Academy   : ${diag.user.academy_id || '(none)'}` : '',
    `Request   : ${diag.request.method} ${diag.request.url}`,
    diag.response ? `Status    : ${diag.response.status}` : '',
    diag.response ? `Backend   : ${diag.response.detail}` : '',
    diag.network_error ? `Network   : ${diag.network_error}` : '',
    `Browser   : ${diag.user_agent}`,
    ``,
    `(auto-generated by Out-Play error reporter)`,
  ].filter(Boolean).join('\n');
}

async function reportError(err, fallback) {
  const diag = buildDiagnostic(err, fallback);
  const body = formatDiagnostic(diag);

  // 1. Copy to clipboard. Wrap in try because some browsers reject silently.
  let copied = false;
  try {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(body);
      copied = true;
    }
  } catch { /* fallback to mailto below */ }

  // 2. Open prefilled mailto. The body is URL-encoded; subject is short.
  const subject = `Out-Play error — ${diag.response ? diag.response.status : 'Network'} on ${diag.request.url || diag.page_url}`;
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  try {
    window.location.href = mailto;
  } catch { /* mailto handler blocked */ }

  toast.success(
    copied
      ? 'Report copied to clipboard — paste into WhatsApp/email to your owner'
      : 'Report opened — your email client should be loading now',
    { duration: 4500 }
  );
}

export function apiErrorToast(err, fallback = 'Something went wrong') {
  // Always echo full error for developer triage (DevTools console).
  console.error('[apiErrorToast]', err);

  // Network / no-response case
  if (!err || !err.response) {
    return toast.error(`Network · ${fallback}`, {
      description: err?.message || 'No response from server. Check your connection and try again.',
      action: { label: 'Report', onClick: () => reportError(err, fallback) },
    });
  }

  const status = err.response.status;
  const detail = pickDetail(err.response.data) || fallback;
  const shortDetail = String(detail).slice(0, MAX_DETAIL_LEN);
  const message = `${status} · ${shortDetail}`;

  // 401 / 403 get extra context — those are the ones managers/staff hit most.
  if (status === 401) {
    return toast.error(message, {
      description: 'Your session may have expired — try logging in again.',
      action: { label: 'Report', onClick: () => reportError(err, fallback) },
    });
  }
  if (status === 403) {
    return toast.error(message, {
      description: 'Ask your academy owner to grant the missing permission from HRMS → Employees → Permissions.',
      action: { label: 'Report', onClick: () => reportError(err, fallback) },
    });
  }
  return toast.error(message, {
    action: { label: 'Report', onClick: () => reportError(err, fallback) },
  });
}

export default apiErrorToast;
